import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CreatorsProvider } from "@/context/CreatorsContext";
import HomePage from "@/pages/HomePage";
import ExplorePage from "@/pages/ExplorePage";
import EmbajadorasPage from "@/pages/EmbajadorasPage";
import { ShortsFeed, SingleShort, CreatorShortsProfile } from "@/pages/shorts/ShortsPages";
import { HomeShorts, BuscarPage, TopsPage, HashtagsPage, NichosPage } from "@/pages/PublicTabs";
import AdminShorts from "@/pages/admin/AdminShorts";
import CreatorProfile from "@/pages/CreatorProfile";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCreators from "@/pages/admin/AdminCreators";
import AdminCreatorForm from "@/pages/admin/AdminCreatorForm";
import AdminStats from "@/pages/admin/AdminStats";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminAutoPost from "@/pages/admin/AdminAutoPost";
import AdminAutoPostChannels from "@/pages/admin/AdminAutoPostChannels";
import AdminAutoPostDiagnostics from "@/pages/admin/AdminAutoPostDiagnostics";
import AdminAutoPostQueue from "@/pages/admin/AdminAutoPostQueue";
import AdminAutoPostBroadcast from "@/pages/admin/AdminAutoPostBroadcast";
import AdminBoveda from "@/pages/admin/AdminBoveda";
import AdminCaptionsBoveda from "@/pages/admin/AdminCaptionsBoveda";
import AdminJGFans from "@/pages/admin/AdminJGFans";
import AdminInbox from "@/pages/admin/AdminInbox";
import { JGFansGate, JGFansSuccess, JGFansFeed } from "@/pages/jgfans/JGFansPages";
import AdminBoletin from "@/pages/admin/AdminBoletin";
import AdminTelegramVip from "@/pages/admin/AdminTelegramVip";
import AdminMemberships from "@/pages/admin/AdminMemberships";
import AdminPaymentMethods from "@/pages/admin/AdminPaymentMethods";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

// Modelo pages
import AuthCallback from "@/pages/modelo/AuthCallback";
import ModeloLogin from "@/pages/modelo/ModeloLogin";
import ModeloRegistro from "@/pages/modelo/ModeloRegistro";
import ModeloDashboard from "@/pages/modelo/ModeloDashboard";
import ModeloEditarPerfil from "@/pages/modelo/ModeloEditarPerfil";
import CompletarPerfil from "@/pages/modelo/CompletarPerfil";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This must be checked synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomeShorts />} />
      <Route path="/info" element={<HomePage />} />
      <Route path="/buscar" element={<BuscarPage />} />
      <Route path="/hashtags" element={<HashtagsPage />} />
      <Route path="/tops" element={<TopsPage />} />
      <Route path="/nichos" element={<NichosPage />} />
      <Route path="/explorar" element={<ExplorePage />} />
      <Route path="/embajadoras" element={<EmbajadorasPage />} />
      <Route path="/shorts" element={<ShortsFeed />} />
      <Route path="/shorts/:shortId" element={<SingleShort />} />
      <Route path="/:username/shorts" element={<CreatorShortsProfile />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/creadoras" element={<ProtectedRoute><AdminCreators /></ProtectedRoute>} />
      <Route path="/admin/creadoras/nueva" element={<ProtectedRoute><AdminCreatorForm /></ProtectedRoute>} />
      <Route path="/admin/creadoras/editar/:id" element={<ProtectedRoute><AdminCreatorForm /></ProtectedRoute>} />
      <Route path="/admin/estadisticas" element={<ProtectedRoute><AdminStats /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/creadoras/:username/auto-posting" element={<ProtectedRoute><AdminAutoPost /></ProtectedRoute>} />
      <Route path="/admin/auto-posting" element={<ProtectedRoute><AdminAutoPostChannels /></ProtectedRoute>} />
      <Route path="/admin/auto-posting/diagnostico" element={<ProtectedRoute><AdminAutoPostDiagnostics /></ProtectedRoute>} />
      <Route path="/admin/auto-posting/cola" element={<ProtectedRoute><AdminAutoPostQueue /></ProtectedRoute>} />
      <Route path="/admin/auto-posting/broadcast" element={<ProtectedRoute><AdminAutoPostBroadcast /></ProtectedRoute>} />
      <Route path="/admin/boveda" element={<ProtectedRoute><AdminBoveda /></ProtectedRoute>} />
      <Route path="/admin/captions" element={<ProtectedRoute><AdminCaptionsBoveda /></ProtectedRoute>} />
      <Route path="/admin/jgfans" element={<ProtectedRoute><AdminJGFans /></ProtectedRoute>} />
      <Route path="/admin/mensajes" element={<ProtectedRoute><AdminInbox /></ProtectedRoute>} />
      <Route path="/admin/shorts" element={<ProtectedRoute><AdminShorts /></ProtectedRoute>} />

      {/* JGFans public routes (must come BEFORE /:username catch-all) */}
      <Route path="/jgfans/:username" element={<JGFansGate />} />
      <Route path="/jgfans/:username/exito" element={<JGFansSuccess />} />
      <Route path="/jgfans/:username/feed" element={<JGFansFeed />} />
      <Route path="/admin/boletin" element={<ProtectedRoute><AdminBoletin /></ProtectedRoute>} />
      <Route path="/admin/telegram-vip" element={<ProtectedRoute><AdminTelegramVip /></ProtectedRoute>} />
      <Route path="/admin/membresias" element={<ProtectedRoute><AdminMemberships /></ProtectedRoute>} />
      <Route path="/admin/metodos-pago" element={<ProtectedRoute><AdminPaymentMethods /></ProtectedRoute>} />
      
      {/* Modelo Routes */}
      <Route path="/modelo/login" element={<ModeloLogin />} />
      <Route path="/modelo/registro" element={<ModeloRegistro />} />
      <Route path="/modelo/completar-perfil" element={<CompletarPerfil />} />
      <Route path="/modelo/dashboard" element={<ModeloDashboard />} />
      <Route path="/modelo/crear-perfil" element={<ModeloEditarPerfil />} />
      <Route path="/modelo/editar-perfil" element={<ModeloEditarPerfil />} />
      
      {/* Creator Profile - Must be last (catch-all for usernames) */}
      <Route path="/:username" element={<CreatorProfile />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <CreatorsProvider>
        <div className="min-h-screen bg-background">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="bottom-center" />
        </div>
      </CreatorsProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
